import { ApiEndpoint } from "@/app/apiEndpoint";
import { ATTACH_TYPE } from "@/app/const";
import axios from "@/app/http/axios";
import { UploadResponse, UrlResponse } from "@/interface/FileUpload.interface";

export default class FileService {
  constructor() {}

  getMimeTypeFromFilename(filename: string) {
    // Define a mapping of extensions to MIME types
    const mimeTypes = {
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      bmp: "image/bmp",
      webp: "image/webp",
      pdf: "application/pdf",
      txt: "text/plain",
      html: "text/html",
      htm: "text/html",
      mp3: "audio/mpeg",
      wav: "audio/wav",
      ogg: "audio/ogg",
      mp4: "video/mp4",
      webm: "video/webm",
      avi: "video/x-msvideo",
      zip: "application/zip",
      tar: "application/x-tar",
      rar: "application/x-rar-compressed",
      json: "application/json",
      xml: "application/xml",
      csv: "text/csv",
    } as any;

    // Extract the file extension (the part after the last dot)
    const extension = filename.split(".")?.pop()?.toLowerCase() as
      | string
      | undefined;

    // Return the corresponding MIME type or a default value
    return extension
      ? mimeTypes[extension] || "application/octet-stream"
      : "application/octet-stream"; // Default to binary data if unknown
  }

  blobToBase64(blob: any) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  async urlToBase64(url: string) {
    if (!url) {
      return undefined;
    }

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const blob = await response.blob();
      return this.blobToBase64(blob);
    } catch (error) {
      console.error("Error fetching or converting the URL:", error);
      return undefined;
    }
  }

  dataURLToBlob(dataURL: string): Blob {
    const [header, base64] = dataURL.split(",");
    const mimeType = header.match(/:(.*?);/)?.[1] || "";
    const binary = atob(base64);
    const array = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
      array[i] = binary.charCodeAt(i);
    }

    return new Blob([array], { type: mimeType });
  }

  async loadImageUrl(url: string) {
    try {
      const resUrl = await this.getUrl([{ objectName: url }]);
      if (resUrl && resUrl.status === 201) {
        const data = resUrl.data as UrlResponse;
        return data.urls[0].url;
      }
    } catch (e) {
      return undefined;
    }
  }

  async viewFileInEditMode(attach: any) {
    if (attach.type === ATTACH_TYPE.NEW) {
      await this.viewFileAfterUpload(attach.blob);
    } else if (attach.type === ATTACH_TYPE.SAVED) {
      await this.viewFileAlreadySaved(attach.url, attach.fileName);
    }
  }

  async viewFileAfterUpload(dataUrl: string | undefined) {
    if (dataUrl) {
      let blob = this.dataURLToBlob(dataUrl);
      let base64Data = await this.blobToBase64(blob);
      this.openInNewTab(base64Data);
    }
  }

  async viewFileAlreadySaved(url: string, fileName: string) {
    const newTab = window.open('', '_blank'); // open immediately

    const resUrl = await this.getUrl([{ objectName: url }]);
    if (resUrl && resUrl.status === 201) {
      const data = resUrl.data as UrlResponse;
      if (data.urls[0]?.url) {
        newTab?.location.assign(data.urls[0].url);
      } else {
        newTab?.close(); // if no URL, close the blank tab
      }
    } else {
      newTab?.close(); // also close if request failed
    }
  }

  openInNewTab(base64Data: any, fileName?: string) {
    if (fileName) {
      const mimeType = this.getMimeTypeFromFilename(fileName);
      if (base64Data.startsWith("data:binary/octet-stream")) {
        base64Data = base64Data.replace("binary/octet-stream", mimeType);
      }
    }
    const newTab = window.open();
    if (newTab) {
      newTab.document.write(`
            <iframe 
                src="${base64Data}" 
                width="100%" 
                height="100%" 
                style="border:none;">
            </iframe>
        `);
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} B`;
    } else if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(0)} KB`;
    } else {
      return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
    }
  }

  isNewAttachment(type: string) {
    return type === ATTACH_TYPE.NEW;
  }
  isSavedAttachment(type: string) {
    return type === ATTACH_TYPE.SAVED;
  }

  async upload(value: File[]) {
    const formData = new FormData();
    value.forEach((o) => {
      formData.append("files", o);
    });
    const path = ApiEndpoint.File.UPLOAD;
    try {
      const response = await axios.post<UploadResponse>(`${path}`, formData);
      return response;
    } catch (error: any) {
      throw error;
    }
  }

  async getUrl(value: any[]) {
    const path = ApiEndpoint.File.GET_URL;
    try {
      const response = await axios.post<UrlResponse>(`${path}`, value);
      return response;
    } catch (error: any) {
      throw error;
    }
  }
}
